import { reducedStream, reducer, streamReducer } from '@ardoq/rxbeach';
import { map } from 'rxjs';
import { setStateProperty } from '@ardoq/common-helpers';
import { Features } from '@ardoq/api-types';
import { featureContext$ } from 'streams/featureContext$';
import { featureOperations } from '@ardoq/features';
import {
  sendMessageWithHistory,
  openFullscreenChatAndSend,
  initializeNewChat,
  closeChatWindow,
  setFeedbackInputValue,
  showAdditionalFeedbackInput,
  showFeedbackForm,
  hideFeedbackForm,
  showFeedbackFormSuccessMessage,
  toggleReasoningVisible,
  historicChatLoaded,
  chatTimedOut,
  sendMessageFailed,
  appendThinkingStep,
  appendUserFriendlyChatError,
  genericChatErrorReceived,
  appendInfoMessage,
  dismissFeedbackCard,
  setCurrentActiveRequest,
  agentRunRestored,
  agentRunResumeFailed,
  interactionResolved,
} from './actions';
import {
  assistantMessagesOperations,
  defaultAssistantMessagesState,
  type AssistantMessagesState,
} from './assistantMessagesOperations';
import type { DecoratedSendMessageRequest } from './types';
import {
  websocketChatMessageContent$,
  websocketChatMessageDone$,
  websocketChatMessageToolResult$,
} from './websocketStreams$';
import { activeUserFacingInteractionRequested$ } from './interactionStreams$';
import { handlePlanEvent } from './planMessageOperations';
import { activePlanEvent$ } from './planStreams$';

export type { AssistantMessagesState } from './assistantMessagesOperations';
export { defaultAssistantMessagesState } from './assistantMessagesOperations';

const handleOpenFullscreenChatAndSend = (
  state: AssistantMessagesState,
  payload: DecoratedSendMessageRequest
): AssistantMessagesState =>
  assistantMessagesOperations.handleSendMessage(
    assistantMessagesOperations.resetMessages(state),
    payload
  );

export const assistantMessages$ = reducedStream(
  'assistantMessages',
  defaultAssistantMessagesState,
  [
    // New chat (cross-cutting) — reset messages
    reducer(initializeNewChat, assistantMessagesOperations.resetMessages),

    // Send message (cross-cutting)
    reducer(
      sendMessageWithHistory,
      assistantMessagesOperations.handleSendMessage
    ),
    reducer(openFullscreenChatAndSend, handleOpenFullscreenChatAndSend),
    reducer(setCurrentActiveRequest, setStateProperty('_currentActiveRequest')),

    // WebSocket events (cross-cutting)
    streamReducer(activePlanEvent$, handlePlanEvent),
    streamReducer(
      websocketChatMessageContent$,
      assistantMessagesOperations.handleMessageContent
    ),
    streamReducer(
      websocketChatMessageToolResult$,
      assistantMessagesOperations.handleToolResult
    ),
    streamReducer(
      websocketChatMessageDone$,
      assistantMessagesOperations.setIsCurrentChatMessageDone
    ),
    reducer(
      genericChatErrorReceived,
      assistantMessagesOperations.setSendMessageError
    ),

    // User-facing interactions
    streamReducer(
      activeUserFacingInteractionRequested$,
      assistantMessagesOperations.handleUserInputRequested
    ),
    reducer(
      interactionResolved,
      assistantMessagesOperations.handleInteractionResolved
    ),
    reducer(
      appendUserFriendlyChatError,
      assistantMessagesOperations.handleAppendUserFriendlyChatError
    ),
    reducer(appendThinkingStep, assistantMessagesOperations.appendThinkingStep),
    reducer(
      appendInfoMessage,
      assistantMessagesOperations.handleAppendInfoMessage
    ),
    reducer(
      dismissFeedbackCard,
      assistantMessagesOperations.handleDismissFeedbackCard
    ),

    // Send message failed (HTTP-level error before any WebSocket event)
    reducer(
      sendMessageFailed,
      assistantMessagesOperations.handleSendMessageFailed
    ),

    // Timeout
    reducer(chatTimedOut, assistantMessagesOperations.handleChatTimedOut),

    // Feedback form
    reducer(
      setFeedbackInputValue,
      assistantMessagesOperations.handleSetFeedbackInputValue
    ),
    reducer(
      showAdditionalFeedbackInput,
      assistantMessagesOperations.handleShowAdditionalFeedbackInput
    ),
    reducer(
      showFeedbackForm,
      assistantMessagesOperations.handleShowFeedbackForm
    ),
    reducer(
      hideFeedbackForm,
      assistantMessagesOperations.handleHideFeedbackForm
    ),
    reducer(
      showFeedbackFormSuccessMessage,
      assistantMessagesOperations.handleShowFeedbackFormSuccessMessage
    ),

    // Reasoning
    reducer(
      toggleReasoningVisible,
      assistantMessagesOperations.handleToggleReasoningVisible
    ),

    // Reset (cross-cutting)
    reducer(closeChatWindow, assistantMessagesOperations.resetMessages),

    // Historic chat (cross-cutting)
    reducer(
      historicChatLoaded,
      assistantMessagesOperations.handleHistoricChatLoaded
    ),
    reducer(
      agentRunRestored,
      assistantMessagesOperations.handleAgentRunRestored
    ),
    reducer(
      agentRunResumeFailed,
      assistantMessagesOperations.handleAgentRunWatchRestoreFailed
    ),

    // Denormalized feature flag for JSON parsing in message reducers
    streamReducer(
      featureContext$.pipe(
        map(featureContext =>
          featureOperations.hasFeature(
            featureContext,
            Features.AI_TMP_PYTHON_AGENTS
          )
        )
      ),
      setStateProperty('_hasPythonAgentsFeature')
    ),
  ]
);
